import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import classNames from "classnames";
import { Link } from "gatsby";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { getCurrentPath } from "../../../utils/get-params-value";
import "./collapsible-menu-item.scss";

const PREFIX = "CollapsibleMenuItem";

const classes = {
  root: `${PREFIX}-root`,
  expanded: `${PREFIX}-expanded`,
  root2: `${PREFIX}-root2`,
  content: `${PREFIX}-content`,
  expanded2: `${PREFIX}-expanded2`,
  root3: `${PREFIX}-root3`,
};

const Root = styled("div")(() => ({
  [`& .${classes.root}`]: {
    backgroundColor: "transparent",
    border: "1px solid rgba(0, 0, 0, .125)",
    boxShadow: "none",
    color: "#fff",
    "&:not(:last-child)": {
      borderBottom: 0,
    },
    "&:before": {
      display: "none",
    },
  },

  [`& .${classes.expanded}`]: {
    "&:first-of-type": {
      marginBottom: 0,
    },
  },

  [`& .${classes.root2}`]: {
    backgroundColor: "rgba(0, 0, 0, .03)",
    borderBottom: "1px solid rgba(0, 0, 0, .125)",
    marginBottom: -1,
    padding: 0,
    minHeight: 56,
  },

  [`& .${classes.content}`]: {
    "& .collapsible-menu-item__expand-icon": {
      transform: "rotate(0deg)",
      transition: "transform .2s ease-in-out",
    },
    margin: 0,
  },

  [`& .${classes.content}.MuiAccordionSummary-content`]: {
    margin: 0,
  },

  [`& .${classes.expanded2}`]: {
    "& .collapsible-menu-item__expand-icon": {
      transform: "rotate(180deg)",
    },
  },

  [`& .${classes.root3}`]: {
    padding: 0,
    backgroundColor: "transparent",
    "& a.side-bar-item": {
      "&:hover": {
        backgroundColor: "#a2a8b1",
      },
      padding: "0.5rem 1rem 0.5rem 3rem",
      color: "#fff",
    },
  },
}));

const CollapsibleMenuItem = ({
  icon,
  title,
  href,
  subItems = [],
  isActive = false,
  sidebarCollapse = false,
  onSubItemClick,
}) => {
  const [expanded, setExpanded] = useState(false);
  const pathName = getCurrentPath();
  const url = `/${href}`;
  // Auto-expand if any sub-item is active
  useEffect(() => {
    if (!sidebarCollapse) {
      const hasActiveSubItem = subItems.some((subItem) =>
        pathName.includes(subItem.href)
      );
      setExpanded(hasActiveSubItem);
    }
  }, [pathName, subItems, sidebarCollapse]);

  const handleChange = (event, isExpanded) => {
    if (!sidebarCollapse) {
      setExpanded(isExpanded);
    }
  };

  const handleSubItemClick = (subItem) => {
    if (onSubItemClick) {
      onSubItemClick(subItem);
    }
  };

  return (
    <Root className="collapsible-menu-item">
      <Accordion
        className="position-relative"
        square
        expanded={expanded}
        onChange={handleChange}
        classes={{
          root: classes.root,
          expanded: classes.expanded,
        }}
      >
        <AccordionSummary
          aria-controls={`${PREFIX}-content`}
          id={`${PREFIX}-header`}
          classes={{
            root: classes.root2,
            content: classes.content,
            expanded: classes.expanded2,
          }}
        >
          <div
            className={classNames(
              "side-bar-item collapsible-menu-item__header",
              {
                selected: isActive,
              }
            )}
          >
            <div className="side-bar-item__icon">{icon}</div>
            {!sidebarCollapse && (
              <>
                <div className="side-bar-item__title">{title}</div>
                <ExpandMoreIcon
                  className="collapsible-menu-item__expand-icon"
                  sx={{ color: "white" }}
                />
              </>
            )}
          </div>
        </AccordionSummary>
        {!sidebarCollapse && (
          <AccordionDetails
            className="collapsible-menu-item__details"
            classes={{
              root: classes.root3,
            }}
          >
            <div className="w-100">
              {subItems.map((subItem) => {
                const url = `/${subItem.href}`;
                return (
                  <Link
                    key={subItem.href}
                    to={url}
                    className={classNames("collapsible-menu-item__sub-item", {
                      "collapsible-menu-item__sub-item--active":
                        subItem.isActive,
                    })}
                    onClick={() => handleSubItemClick(subItem)}
                  >
                    {subItem.icon && (
                      <div className="side-bar-item__icon">{subItem.icon}</div>
                    )}
                    <div className="side-bar-item__title">{subItem.title}</div>
                  </Link>
                );
              })}
            </div>
          </AccordionDetails>
        )}
      </Accordion>
    </Root>
  );
};

CollapsibleMenuItem.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.string.isRequired,
  href: PropTypes.string,
  subItems: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.node,
      title: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
      isActive: PropTypes.bool,
    })
  ),
  isActive: PropTypes.bool,
  sidebarCollapse: PropTypes.bool,
  onSubItemClick: PropTypes.func,
};

export default CollapsibleMenuItem;
